/**
 * A/B Testing core utilities
 * Calls the PHP API on vexub.fr
 */

const AB_API_BASE = "https://vexub.fr/api/abtest";

/**
 * Get variant for a single test
 */
export async function getVariant(
  userId: string,
  testName: string,
  token?: string,
  country?: string
): Promise<string | null> {
  const params = new URLSearchParams({ test: testName, user_id: userId });
  if (token) params.set("token", token);
  if (country) params.set("country", country);

  try {
    const res = await fetch(`${AB_API_BASE}/get_variant.php?${params}`, {
      cache: "no-store",
    });
    const data = await res.json();
    return data.variant ?? null;
  } catch {
    return null;
  }
}

/**
 * Get variants for multiple tests at once (batch)
 */
export async function getVariants(
  userId: string,
  testNames: string[],
  token?: string,
  country?: string
): Promise<Record<string, string | null>> {
  const params = new URLSearchParams({
    tests: testNames.join(","),
    user_id: userId,
  });
  if (token) params.set("token", token);
  if (country) params.set("country", country);

  try {
    const res = await fetch(`${AB_API_BASE}/get_variant.php?${params}`, {
      cache: "no-store",
    });
    const data = await res.json();
    return data.variants ?? {};
  } catch {
    return {};
  }
}

/**
 * Record an A/B event (impression or conversion)
 */
export async function recordABEvent(
  userId: string,
  testName: string,
  type: "impression" | "conversion",
  extra?: { event?: string; page_path?: string; metadata?: Record<string, unknown> }
): Promise<void> {
  try {
    await fetch(`${AB_API_BASE}/record_event.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: userId,
        test: testName,
        type,
        ...extra,
      }),
    });
  } catch {
    // Silently fail — don't break the app
  }
}

/**
 * Get variant + record impression in one call (for server components)
 */
export async function getVariantAndImpress(
  userId: string,
  testName: string,
  pagePath?: string,
  token?: string
): Promise<string | null> {
  const variant = await getVariant(userId, testName, token);
  if (variant) {
    await recordABEvent(userId, testName, "impression", { page_path: pagePath });
  }
  return variant;
}

/**
 * Deterministic hash matching PHP's crc32 — for middleware (no API call)
 */
export function abHashVariant(
  userId: string,
  testName: string,
  variants: Record<string, number>
): string {
  const hash = Math.abs(crc32(`${userId}|${testName}`));
  const bucket = hash % 100;

  let cumulative = 0;
  for (const [name, weight] of Object.entries(variants)) {
    cumulative += weight;
    if (bucket < cumulative) {
      return name;
    }
  }
  return Object.keys(variants).pop()!;
}

/**
 * CRC32 implementation matching PHP's crc32()
 */
function crc32(str: string): number {
  let crc = 0xffffffff;
  for (let i = 0; i < str.length; i++) {
    crc ^= str.charCodeAt(i);
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}
